import React, { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  UserCircleIcon,
  Squares2X2Icon,
  ArrowRightStartOnRectangleIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
} from '@headlessui/react';

const getInitials = (name) => {
  if (!name) return '';
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return (first + last).toUpperCase();
};

const Avatar = ({ avatarUrl, initials, size = 'sm' }) => {
  const dims = size === 'lg' ? 'h-11 w-11 text-sm' : 'h-8 w-8 text-[12px]';

  if (avatarUrl) {
    return (
      <img
        key={avatarUrl}
        src={avatarUrl}
        alt=""
        className={`${dims} rounded-full object-cover shrink-0`}
        loading="eager"
      />
    );
  }
  if (initials) {
    return (
      <span
        aria-hidden
        className={`${dims} rounded-full grid place-items-center font-bold text-[#4a3a16] shrink-0`}
        style={{ background: 'linear-gradient(135deg,#FFE7B8,#FFBD59)' }}
      >
        {initials}
      </span>
    );
  }
  return <UserCircleIcon className={`${dims} text-gray-400 shrink-0`} />;
};

const ProfileButton = ({ avatarUrl, name, notificationCount = 0, onLogout }) => {
  const { t } = useTranslation();
  const initials = getInitials(name);

  return (
    <Popover className="relative">
      <PopoverButton as={Fragment}>
        <button
          type="button"
          className="relative inline-flex items-center gap-2 bg-white border border-black/10 rounded-full py-1 pl-1 pr-3 hover:shadow-sm transition outline-none"
        >
          {notificationCount > 0 && (
            <span
              aria-hidden
              className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-red-500 border-2 border-white shadow-[0_0_8px_rgba(239,68,68,0.7)]"
            />
          )}

          <Avatar avatarUrl={avatarUrl} initials={initials} />

          {name && (
            <span className="text-[13px] font-semibold text-black-01 truncate max-w-[6rem] xl:max-w-[11rem]">
              {name}
            </span>
          )}

          <ChevronDownIcon className="h-3 w-3 text-gray-500" strokeWidth={2} />
        </button>
      </PopoverButton>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-150"
        enterFrom="opacity-0 translate-y-1 scale-95"
        enterTo="opacity-100 translate-y-0 scale-100"
        leave="transition ease-in duration-100"
        leaveFrom="opacity-100 translate-y-0 scale-100"
        leaveTo="opacity-0 translate-y-1 scale-95"
      >
        <PopoverPanel className="absolute right-0 mt-3 w-72 z-20 origin-top-right overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-black/5">
          {/* Header */}
          <div className="flex items-center gap-3 px-4 py-4 bg-gradient-to-br from-brand-softer to-white border-b border-black/5">
            <Avatar avatarUrl={avatarUrl} initials={initials} size="lg" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-ink truncate">
                {name || t('header.profile') || 'Profile'}
              </p>
              <p className="text-xs text-muted mt-0.5">
                {t('header.welcome_back', 'Welcome back')}
              </p>
            </div>
          </div>

          {/* Items */}
          <div className="p-1.5">
            <PopoverButton
              as={Link}
              to="/dashboard"
              className="group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-ink-2 hover:bg-brand-softer transition"
            >
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-brand-soft text-brand-deep">
                <Squares2X2Icon className="h-5 w-5" strokeWidth={2} />
              </span>
              <span className="flex-1">{t('dashboard.title')}</span>
              {notificationCount > 0 && (
                <span className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-red-500 px-1.5 text-[11px] font-semibold text-white shadow-[0_0_8px_rgba(239,68,68,0.6)]">
                  {notificationCount}
                </span>
              )}
            </PopoverButton>

            <div className="my-1 border-t border-black/5" />

            <button
              onClick={onLogout}
              className="group w-full flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-ink-2 hover:bg-gray-50 transition"
            >
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-gray-100 text-gray-600 group-hover:bg-gray-200 group-hover:text-gray-800 transition">
                <ArrowRightStartOnRectangleIcon
                  className="h-5 w-5"
                  strokeWidth={2}
                />
              </span>
              <span className="flex-1 text-left">{t('header.logout')}</span>
            </button>
          </div>
        </PopoverPanel>
      </Transition>
    </Popover>
  );
};

export default ProfileButton;
